/**
 * DigiRasa web display dates.
 * Date:      dd-Mmm-yyyy      → 06-Aug-2026
 * Date+time: dd-Mmm-yyyy hh:mm AM/PM → 06-Aug-2026 12:45 PM
 */

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Parse common API / form values into a Date for display.
 * Date-only ISO (YYYY-MM-DD) is treated as a local calendar day (no UTC shift).
 * @param {string | number | Date | null | undefined} value
 * @returns {Date | null}
 */
export function parseDisplayDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const s = String(value).trim();
  if (!s) return null;

  // YYYY-MM-DD (date-only)
  const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) {
    const y = Number(isoDate[1]);
    const m = Number(isoDate[2]);
    const d = Number(isoDate[3]);
    const local = new Date(y, m - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  // YYYY-MM-DDTHH:mm... or space-separated datetime (no timezone).
  // DigiRasa API stores UTC as naive ISO — treat as UTC so display is client-local.
  const isoDateTime = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/);
  if (isoDateTime && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    const sec = isoDateTime[6] != null ? isoDateTime[6] : '00';
    const utc = new Date(
      `${isoDateTime[1]}-${isoDateTime[2]}-${isoDateTime[3]}T${isoDateTime[4]}:${isoDateTime[5]}:${sec}Z`,
    );
    return Number.isNaN(utc.getTime()) ? null : utc;
  }

  // DD-MM-YYYY or DD/MM/YYYY
  const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const local = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(local.getTime()) ? null : local;
  }

  // DD-Mmm-YYYY or DD/Mmm/YYYY (e.g. 06-Aug-2026)
  const dmmy = s.match(/^(\d{1,2})[-/]([A-Za-z]{3})[-/](\d{4})$/);
  if (dmmy) {
    const monthIdx = MONTHS_SHORT.findIndex((m) => m.toLowerCase() === dmmy[2].toLowerCase());
    if (monthIdx >= 0) {
      const local = new Date(Number(dmmy[3]), monthIdx, Number(dmmy[1]));
      return Number.isNaN(local.getTime()) ? null : local;
    }
  }

  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * @param {string | number | Date | null | undefined} value
 * @param {string} [fallback]
 */
export function formatDisplayDate(value, fallback = '') {
  const d = parseDisplayDate(value);
  if (!d) {
    if (value == null || value === '') return fallback;
    return String(value);
  }
  return `${pad2(d.getDate())}-${MONTHS_SHORT[d.getMonth()]}-${d.getFullYear()}`;
}

/**
 * @param {string | number | Date | null | undefined} value
 * @param {string} [fallback]
 */
export function formatDisplayDateTime(value, fallback = '') {
  const d = parseDisplayDate(value);
  if (!d) {
    if (value == null || value === '') return fallback;
    return String(value);
  }
  let hours = d.getHours();
  const minutes = pad2(d.getMinutes());
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${formatDisplayDate(d)} ${pad2(hours)}:${minutes} ${ampm}`;
}

/**
 * Format a time-only value as 12-hour with AM/PM (e.g. 14:30 → 02:30 PM).
 * Accepts HH:mm, HH:mm:ss, already-formatted AM/PM strings, or Date.
 * @param {string | number | Date | null | undefined} value
 * @param {string} [fallback]
 */
export function formatDisplayTime(value, fallback = '') {
  if (value == null || value === '') return fallback;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return fallback;
    let hours = value.getHours();
    const minutes = pad2(value.getMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    if (hours === 0) hours = 12;
    return `${pad2(hours)}:${minutes} ${ampm}`;
  }

  const s = String(value).trim();
  if (!s) return fallback;

  // Already 12-hour with AM/PM
  const ampmMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hours = Number(ampmMatch[1]);
    const minutes = ampmMatch[2];
    const ampm = ampmMatch[3].toUpperCase();
    if (!Number.isFinite(hours) || hours < 1 || hours > 12) return s;
    return `${pad2(hours)}:${minutes} ${ampm}`;
  }

  // 24-hour HH:mm or HH:mm:ss
  const h24 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (h24) {
    let hours = Number(h24[1]);
    const minutes = h24[2];
    if (!Number.isFinite(hours) || hours < 0 || hours > 23) return s;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    if (hours === 0) hours = 12;
    return `${pad2(hours)}:${minutes} ${ampm}`;
  }

  return s;
}

/** Alias used by meal-plan modules. */
export const formatDateForDisplay = formatDisplayDate;
